// Taşeron portalı — doküman analizi (doc-check) durum/sonuç yoklama hook'u
// (api-contract §20.1, `GET /supplier/contractor/document-analyses/{uuid}`; S17-D6/S17-D12).
//
// **Yoklama aralığı tamamen SUNUCU güdümlüdür.** `refetchInterval` her tikte en son yanıtın
// `analysis.pollAfterSeconds`'ını okur; `Queued`/`Running`'de dolu (bugün `3`), terminalde
// (`Succeeded`/`Failed`) KESİN `null` olur → `refetchInterval` `false` döner ve yoklama durur.
// İstemci kendi aralık sabitini, geri-çekilme (backoff) stratejisini ya da "N dakika sonra
// başarısız say" zaman aşımını İCAT ETMEZ — sunucu tarafında en kötü bekleme ~30 dakikadır
// (kaybolan koşu 90 dakikaya kadar terk edilmiş sayılmaz) ve bir istemci zaman aşımı sağlıklı
// süren bir analizi hatalı raporlar (§20, plan S17-D6).
//
// ⚠️ `pollAfterSeconds` SANİYE, `refetchInterval` MİLİSANİYEdir — dönüşüm burada, tek yerde
// yapılır (`* 1000`).
//
// **Sekme gizliyken duraklat, odaklanınca devam et — `refetchOnWindowFocus` global
// `false`'u BİLEREK EZMEZ.** `queryClient.ts:7-23` `refetchOnWindowFocus: false`'u
// depo-geneli ayarlar; bu YALNIZCA "pencere odağa dönünce EK bir refetch tetikle" anlamına
// gelir ve `refetchInterval`'in kendi zamanlayıcısından AYRI bir mekanizmadır.
// `refetchInterval`'in kendisi zaten sekme gizliyken (varsayılan `refetchIntervalInBackground:
// false`) tik'lerini atlar ve sekme yeniden görünür olunca (TanStack'in `visibilitychange`
// dinleyen `focusManager`'ı üzerinden) kaldığı zamanlamadan devam eder — S17-D6'nın istediği
// "duraklat/devam et" davranışı budur ve `refetchOnWindowFocus`'a hiç ihtiyaç duymaz. Bu
// query'de o ayarı `true`'ya çevirmek, S17-D6'nın istemediği bir şeyi yapardı: her sekme
// odak değişiminde sunucu aralığından BAĞIMSIZ, EK bir istek.
//
// Unmount'ta durma TanStack'in varsayılan davranışıdır (aktif gözlemci kalmayınca zamanlayıcı
// temizlenir) — burada ayrıca bir şey yapılmaz.

import {
  type ApiError,
  type DocumentAnalysisDetailDto,
  type DocumentAnalysisDto,
  type DocumentAnalysisListFilter,
  type PagedResult,
  documentAnalysesApi,
  useAuthStore,
} from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { supplierContractorKeys } from './supplierContractorKeys';

export interface UseDocumentAnalysisResult {
  /** Analiz + (terminalse) sonuç. İlk yanıt gelene dek `undefined`. */
  analysis: DocumentAnalysisDetailDto | undefined;
  /** İlk yanıt henüz gelmedi (ilk yükleme; yoklama tikleri bunu bir daha `true` yapmaz). */
  isPending: boolean;
  /** Herhangi bir GET sürüyor — ilk yükleme ya da bir yoklama tiki. */
  isFetching: boolean;
  /** Son GET hatası (§20: durum ucu asla `503` dönmez — yalnız `404 DOCUMENT_ANALYSIS_NOT_FOUND`
   * ya da kapsam/ağ hatası olabilir). */
  error: ApiError | null;
  /** Manuel yeniden dene (örn. geçici ağ hatası sonrası kullanıcı eylemi). */
  refetch: () => void;
}

/**
 * `GET /supplier/contractor/document-analyses/{uuid}` yoklaması. `analysisUuid` yokken
 * (henüz bir analiz başlatılmadıysa) sorgu kapalıdır.
 */
export function useDocumentAnalysis(analysisUuid: string | null): UseDocumentAnalysisResult {
  const companyId = useAuthStore((s) => s.principal?.companyId ?? null);

  const query = useQuery<DocumentAnalysisDetailDto, ApiError>({
    queryKey: supplierContractorKeys.analysis(companyId, analysisUuid ?? ''),
    queryFn: () => documentAnalysesApi.getDocumentAnalysis(analysisUuid as string),
    enabled: analysisUuid != null && companyId != null,
    refetchInterval: (q) => {
      const pollAfterSeconds = q.state.data?.analysis.pollAfterSeconds;
      // Sunucu aralığın KENDİSİNİN otoritesidir (S17-D6) — burada bir varsayılan aralık İCAT
      // edilmez. Ama `0` `refetchInterval` için geçerli bir değerdir (query-core `isValidTimeout`:
      // `>= 0`); sunucu `0` dönerse istemci sıkı bir yoklama döngüsüne girer. 1 saniyelik taban
      // sunucunun aralığını DEĞİŞTİRMEZ, yalnız yozlaşmış bir yanıta karşı korur (S17 güvenlik
      // incelemesi F5).
      return pollAfterSeconds != null ? Math.max(1000, pollAfterSeconds * 1000) : false;
    },
  });

  return {
    analysis: query.data,
    isPending: query.isPending,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}

// ============================================================================
// Analiz LİSTESİ (api-contract §20.3, YENİ S19, `GET .../document-analyses`)
// ============================================================================
//
// Sekmesini/uuid'ini kaybeden istemcinin kurtarma ucu (S19-D5 · AC11 · AC12). Varsayılan
// olarak bu hook YOKLAMA YAPMAZ — tek işi bir sayfa yüklemesinde (ya da süzgeç değişiminde) o
// ANKİ listeyi bir kez çekmektir. `active` sonucunda bulunan bir uuid'in DURUMUNUN sürüp
// gitmesi/yoklanması (kabul/ret formunun okuduğu DETAY) yukarıdaki `useDocumentAnalysis`'e
// devredilir — tek analizlik sunucu güdümlü yoklama TEK yerde yaşar, burada TEKRARLANMAZ.
// `DocumentAnalysisProvider` bunu `active` süzgeciyle sayfa yenilemesinde tray'in yoklamayı
// yeniden bağlaması için kullanır; `awaiting-review` süzgeci Belgeler'in bekleyen şeridi (D7)
// içindir.
//
// **`poll` (YENİ, S25 web kod incelemesi #1).** LİSTENİN KENDİSİNİN sürüp gitmesi gereken TEK
// durum — `DocumentAnalysisInboxPage`'in `active` rayı ("analiz ediliyor" satırları): bir
// analiz Queued/Running'den çıktığı ANDA bu liste onu artık DÖNDÜRMEZ (§20.3), rayın bunu fark
// etmesi için listenin kendisinin yenilenmesi gerekir. Yine SUNUCU GÜDÜMLÜ — dönen
// `items[].pollAfterSeconds`in EN KÜÇÜĞÜ (birden çok aktif analiz farklı aralıkta olabilir)
// `refetchInterval` yapılır; hiçbir öğe pollable değilse (liste boş ya da hepsi `null` —
// `active` süzgeci için olağan DEĞİLDİR ama savunmacı) durur. İstemci kendi aralığını/zaman
// aşımını İCAT ETMEZ (`useDocumentAnalysis`'in AYNI kuralı, dosya üstü notu).

export interface UseDocumentAnalysisListResult {
  items: DocumentAnalysisDto[];
  /** Kapsam uygulanmış, `status` süzgecinden ÖNCEKİ toplam (§3). */
  total: number;
  /** Süzgeç SONRASI toplam. */
  filteredTotal: number;
  /** İlk yanıt henüz gelmedi. */
  isPending: boolean;
  isFetching: boolean;
  error: ApiError | null;
  refetch: () => void;
}

/**
 * `GET /supplier/contractor/document-analyses` (§20.3) — çağıranın kendi taşeronunun
 * analizleri, `status` süzgeciyle (`awaiting-review` | `active` | `all` | `archived` — YENİ
 * S25-A4). **Bu uç `503` DÖNMEZ** ve ne yapılandırmaya ne firma allowlist'ine bakar (analiz
 * TARİHSEL bir kayıttır) — `error` yalnız kapsam/ağ hatasını taşır.
 *
 * `pageSize` verilmezse sunucu varsayılanı (§3, `50`) uygulanır — sorgu anahtarı da bu durumda
 * `pageSize` TAŞIMAZ (geriye dönük AYNI anahtar, `PendingAnalysesStrip`/`StrandedAnalysesStrip`
 * gibi varsayılan çağıranlarla önbellek PAYLAŞILIR). `pageSize` AÇIKÇA verilirse ayrı bir
 * önbellek girdisi açılır (§Anahtarlar notu, `supplierContractorKeys.analyses`) — böylece bir
 * çağıranın daha geniş sayfa istemesi başka bir çağıranın (varsayılan boyuttaki) önbelleğini
 * EZMEZ (S25 web kod incelemesi #5).
 */
export function useDocumentAnalysisList(
  filter: DocumentAnalysisListFilter,
  options: { enabled?: boolean; pageSize?: number; poll?: boolean } = {},
): UseDocumentAnalysisListResult {
  const companyId = useAuthStore((s) => s.principal?.companyId ?? null);
  const { pageSize, poll = false } = options;

  const query = useQuery<PagedResult<DocumentAnalysisDto>, ApiError>({
    queryKey: supplierContractorKeys.analyses(companyId, filter, pageSize),
    queryFn: () => documentAnalysesApi.listDocumentAnalyses({ status: filter, pageSize }),
    enabled: companyId != null && (options.enabled ?? true),
    refetchInterval: poll
      ? (q) => {
          const items = q.state.data?.items ?? [];
          const pollable = items
            .map((item) => item.pollAfterSeconds)
            .filter((s): s is number => s != null);
          if (pollable.length === 0) {
            return false;
          }
          // Aynı taban koruması — `useDocumentAnalysis`in AYNI notu (dosya üstü, ⚠️).
          return Math.max(1000, Math.min(...pollable) * 1000);
        }
      : false,
  });

  return {
    items: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    filteredTotal: query.data?.filteredTotal ?? 0,
    isPending: query.isPending,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}
